"use client";

import BupiLink from "@/components/atoms/link";
import { useTranslations } from "next-intl";
import Image from "@/components/atoms/image";
import React, { JSX } from "react";

export interface CardItemProps {
  style: "image" | "green" | "orange";
  image?: {
    url: string;
    alt: string;
  };
  title?: string;
  description?: string;
  ctaButton?: {
    label: string;
    url: string;
    style: string;
    target: string;
  };
  index?: number;
}

export const CardItem = ({
  style,
  title,
  description,
  ctaButton,
  index = 0,
  image,
}: CardItemProps): JSX.Element => {
  const t = useTranslations("CardGridSection");

  const marginTop =
    index === 1 ? "xl:mt-[32px]" : index === 2 ? "xl:mt-[64px]" : "";

  if (style === "image") {
    return (
      <div
        className={`h-[360px] ${marginTop}`}
      >
        <div className="relative h-full w-full overflow-hidden rounded-lg">
          <picture className="absolute w-full h-full">
            <source
              media="(max-width: 767px)"
              srcSet={"/assets/card-section/desktop.png"}
            />

            <source
              media="(max-width: 1023px)"
              srcSet={"/assets/card-section/tablet.png"}
            />

            <Image
              fill
              src={"/assets/card-section/desktop.png"}
              alt={t("backgroundIllustration")}
              className="w-full h-full object-cover"
            />
          </picture>
          {image?.url && (
            <Image
              fill
              src={image.url}
              alt={image.alt || t("Illustration")}
              className="w-full h-full object-contain object-bottom"
            />
          )}
        </div>
      </div>
    );
  }

  const bgColor = {
    green: "bg-[#cfe5e0]",
    orange: "bg-[#fadacb]",
  }[style];

  return (
    <div
      className={`h-[360px] ${marginTop}`}
      role="region"
      aria-label={title || `Card ${index + 1}`}
    >
      <div
        className={`h-full ${bgColor} px-32 py-[44px] flex flex-col justify-between`}
      >
        <div className="space-y-[8px]">
          {title && (
            <h2
              id={`card-title-${index}`}
              className="text-xl-bold  md:text-2xl-bold"
            >
              {title}
            </h2>
          )}
          {description && <p className="text-m-regular">{description}</p>}
        </div>
        {ctaButton && (
          <div className="">
            <BupiLink
              label={ctaButton.label}
              url={ctaButton.url}
              hasIcon
              removestyle
              className="text-neutral-900 !fill-neutral-900"
              aria-label={`${ctaButton.label} - ${title ?? ""}`}
              target={ctaButton.target || "_self"}
            />
          </div>
        )}
      </div>
    </div>
  );
};
